import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getrapportwell } from '../../Redux/Actions/RapportActions';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import './WellnessStats.css';

const WellnessStats = () => {
  const dispatch = useDispatch();
  
  useEffect(() => {
    dispatch(getrapportwell());
  }, [dispatch]);

  const rapports = useSelector((state) => state.RapportReducer.rapports || []);

  const [localisation, setLocalisation] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Options pour le select de localisation (identique à ListRapports)
  const locations = [
    "Champion Lafayette", "Central Park", "Palmarium", "Zaphir", "Tunis City",
    "Azur City", "Claridge", "TP-Manar City", "TP-Tunisia Mall", "TP-Jardins D'el Menzah",
    "Manar City", "Tunisia Mall", "Shell-Grombalia", "Shell-Sidi Khlifa", "Agil-Sidi Khlifa",
    "Agil-Bourjin", "Agil-Jem", "Sfax Palace", "Djerba Mall", "Bourgo Mall", "TP-Bourgo Mall",
    "mickey Hammamet"
  ];

  // Enrichir les données avec le calcul de la moyenne journalière entre deux collectes
  const enrichedData = useMemo(() => {
    if (!rapports || rapports.length === 0) return [];

    // 1. Grouper par emplacement et trier par date
    const byLocation = {};
    const sortedRapports = [...rapports].sort((a, b) => new Date(a.DateCollect) - new Date(b.DateCollect));
    
    sortedRapports.forEach(item => {
      if (!byLocation[item.localisation]) {
        byLocation[item.localisation] = [];
      }
      byLocation[item.localisation].push(item);
    });

    // 2. Calculer les périodes et les moyennes journalières
    const enriched = [];
    Object.keys(byLocation).forEach(loc => {
      const items = byLocation[loc];
      for (let i = 0; i < items.length; i++) {
        const current = items[i];
        let daysSinceLast = 0;
        let dailyAmount = 0;
        let dailyAvgPerChair = 0;

        if (i > 0) {
          const prev = items[i - 1];
          const prevDate = new Date(prev.DateCollect);
          const currDate = new Date(current.DateCollect);
          // On s'assure de ne garder que la date (sans l'heure) pour le calcul des jours
          prevDate.setHours(0,0,0,0);
          currDate.setHours(0,0,0,0);
          
          const diffTime = currDate - prevDate;
          daysSinceLast = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          if (daysSinceLast > 0) {
            dailyAmount = (current.Amount || 0) / daysSinceLast;
            dailyAvgPerChair = current.ChairNumber > 0 ? dailyAmount / current.ChairNumber : 0;
          }
        }
        
        enriched.push({
          ...current,
          daysSinceLast,
          dailyAmount,
          dailyAvgPerChair,
          prevDate: i > 0 ? items[i - 1].DateCollect : null
        });
      }
    });
    
    return enriched;
  }, [rapports]);

  // Filtrer les données enrichies
  const filteredData = useMemo(() => {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    return enrichedData.filter((entry) => {
      const entryDate = new Date(entry.DateCollect);
      const isLocationMatch = localisation === "All" || entry.localisation === localisation;
      const isDateInRange = (!start || entryDate >= start) && (!end || entryDate <= end);
      return isLocationMatch && isDateInRange;
    }).sort((a, b) => new Date(b.DateCollect) - new Date(a.DateCollect)); // Afficher les plus récents en premier
  }, [enrichedData, localisation, startDate, endDate]);

  // Calculer les KPIs
  const kpis = useMemo(() => {
    let totalCA = 0;
    let totalDepenses = 0;
    let totalChaises = 0;

    filteredData.forEach((item) => {
      totalCA += item.Amount || 0;
      totalDepenses += item.MonthExpenses || 0;
      totalChaises += item.ChairNumber || 0;
    });

    const net = totalCA - totalDepenses;
    const avgPerChair = totalChaises > 0 ? (totalCA / totalChaises) : 0;

    return { totalCA, totalDepenses, net, avgPerChair };
  }, [filteredData]);

  // Préparer les données pour les graphiques
  // 1. CA et Dépenses par Localisation (pour BarChart)
  const dataByLocation = useMemo(() => {
    const map = {};
    filteredData.forEach(item => {
      if (!map[item.localisation]) {
        map[item.localisation] = { loc: item.localisation, CA: 0, Depenses: 0 };
      }
      map[item.localisation].CA += item.Amount || 0;
      map[item.localisation].Depenses += item.MonthExpenses || 0;
    });
    return Object.values(map).sort((a, b) => b.CA - a.CA).slice(0, 10); // Top 10 pour lisibilité
  }, [filteredData]);

  // 2. Tendance temporelle (pour LineChart)
  const dataByDate = useMemo(() => {
    const map = {};
    filteredData.forEach(item => {
      // Grouper par date (YYYY-MM-DD)
      const date = new Date(item.DateCollect).toISOString().split('T')[0];
      if (!map[date]) {
        map[date] = { date, CA: 0, Depenses: 0 };
      }
      map[date].CA += item.Amount || 0;
      map[date].Depenses += item.MonthExpenses || 0;
    });
    return Object.values(map).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [filteredData]);

  // 3. Classement par Emplacement (Meilleur CA par chaise)
  const rankingByLocation = useMemo(() => {
    const map = {};
    filteredData.forEach(item => {
      if (!map[item.localisation]) {
        map[item.localisation] = {
          loc: item.localisation,
          totalCA: 0,
          totalDepenses: 0,
          // Les données étant triées du plus récent au plus ancien, on prend le premier ChairNumber trouvé
          chairCount: item.ChairNumber || 0,
        };
      }
      map[item.localisation].totalCA += item.Amount || 0;
      map[item.localisation].totalDepenses += item.MonthExpenses || 0;
      
      // Si on n'avait pas de chaises, on essaie de prendre une valeur plus ancienne
      if (map[item.localisation].chairCount === 0 && item.ChairNumber > 0) {
        map[item.localisation].chairCount = item.ChairNumber;
      }
    });

    return Object.values(map).map(loc => {
      const caPerChair = loc.chairCount > 0 ? loc.totalCA / loc.chairCount : 0;
      return { ...loc, caPerChair };
    }).sort((a, b) => b.caPerChair - a.caPerChair);
  }, [filteredData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(value);
  };

  return (
    <div className="wellness-dashboard-container">
      <div className="wellness-header">
        <h1>Statistiques Wellness</h1>
        <p>Aperçu des performances, chiffre d'affaires et dépenses d'Entourage.</p>
      </div>

      {/* Filtres */}
      <div className="wellness-filters">
        <div className="filter-group">
          <label>Localisation</label>
          <select value={localisation} onChange={(e) => setLocalisation(e.target.value)}>
            <option value="All">Toutes les localisations</option>
            {locations.map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Date de début</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="filter-group">
          <label>Date de fin</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
      </div>

      {/* KPIs */}
      <div className="wellness-kpis">
        <div className="kpi-card">
          <div className="kpi-title">Chiffre d'Affaires</div>
          <div className="kpi-value primary">{formatCurrency(kpis.totalCA)}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-title">Dépenses</div>
          <div className="kpi-value warning">{formatCurrency(kpis.totalDepenses)}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-title">Résultat Net</div>
          <div className={`kpi-value ${kpis.net >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(kpis.net)}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-title">Moyenne par Chaise</div>
          <div className="kpi-value">{formatCurrency(kpis.avgPerChair)}</div>
        </div>
      </div>

      {/* Graphiques */}
      {(dataByLocation.length > 0 || dataByDate.length > 0) && (
        <div className="wellness-charts">
          {dataByLocation.length > 0 && (
            <div className="chart-container">
              <div className="chart-title">CA vs Dépenses par Emplacement (Top 10)</div>
              <BarChart
                dataset={dataByLocation}
                xAxis={[{ scaleType: 'band', dataKey: 'loc', tickLabelStyle: { angle: 45, textAnchor: 'start', fontSize: 11 } }]}
                series={[
                  { dataKey: 'CA', label: 'CA (TND)', color: '#2196F3' },
                  { dataKey: 'Depenses', label: 'Dépenses (TND)', color: '#FF9800' }
                ]}
                height={320}
                margin={{ left: 60, right: 10, top: 20, bottom: 80 }}
              />
            </div>
          )}
          
          {dataByDate.length > 0 && (
            <div className="chart-container">
              <div className="chart-title">Tendance au fil du temps</div>
              <LineChart
                dataset={dataByDate}
                xAxis={[{ scaleType: 'point', dataKey: 'date' }]}
                series={[
                  { dataKey: 'CA', label: 'CA (TND)', color: '#4CAF50', curve: "catmullRom" },
                  { dataKey: 'Depenses', label: 'Dépenses (TND)', color: '#F44336', curve: "catmullRom" }
                ]}
                height={320}
                margin={{ left: 60, right: 20, top: 20, bottom: 50 }}
              />
            </div>
          )}
        </div>
      )}

      {/* Tableau de classement par emplacement */}
      <div className="wellness-table-container" style={{marginBottom: '40px'}}>
        <h3 style={{marginBottom: '20px', color: '#333'}}>🏆 Classement : Meilleur chiffre par chaise</h3>
        {rankingByLocation.length > 0 ? (
          <table className="wellness-table">
            <thead>
              <tr>
                <th>Rang</th>
                <th>Emplacement</th>
                <th style={{textAlign: 'center'}}>Nb Chaises</th>
                <th>CA Total</th>
                <th>Dépenses Totales</th>
                <th>CA / Chaise</th>
              </tr>
            </thead>
            <tbody>
              {rankingByLocation.map((item, index) => (
                <tr key={index}>
                  <td style={{
                    fontWeight: 'bold', 
                    fontSize: index < 3 ? '1.2em' : '1em',
                    color: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : '#777'
                  }}>
                    #{index + 1}
                  </td>
                  <td style={{fontWeight: '600'}}>{item.loc}</td>
                  <td style={{textAlign: 'center'}}>{item.chairCount}</td>
                  <td style={{color: '#2196F3'}}>{formatCurrency(item.totalCA)}</td>
                  <td style={{color: '#FF9800'}}>{formatCurrency(item.totalDepenses)}</td>
                  <td style={{color: '#9C27B0', fontWeight: 'bold', fontSize: '1.1em'}}>{formatCurrency(item.caPerChair)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{textAlign: 'center', color: '#777', padding: '20px'}}>Aucune donnée disponible pour le classement.</p>
        )}
      </div>

      {/* Tableau détaillé */}
      <div className="wellness-table-container">
        <h3 style={{marginBottom: '20px', color: '#333'}}>Historique des collectes</h3>
        {filteredData.length > 0 ? (
          <table className="wellness-table">
            <thead>
              <tr>
                <th>Date Collecte</th>
                <th>Emplacement</th>
                <th>Période (Jours)</th>
                <th>Nb Chaises</th>
                <th>CA</th>
                <th>Moy. Jour</th>
                <th>Moy. Jour / Chaise</th>
                <th>Dépenses</th>
                <th>Net</th>
                <th>Remarques</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, index) => (
                <tr key={index}>
                  <td>
                    {new Date(item.DateCollect).toLocaleDateString('fr-TN')}
                    {item.prevDate && <div style={{fontSize: '0.8em', color: '#888'}}>Depuis le {new Date(item.prevDate).toLocaleDateString('fr-TN')}</div>}
                  </td>
                  <td>{item.localisation}</td>
                  <td style={{textAlign: 'center'}}>{item.daysSinceLast > 0 ? `${item.daysSinceLast}j` : '-'}</td>
                  <td style={{textAlign: 'center'}}>{item.ChairNumber}</td>
                  <td style={{color: '#2196F3', fontWeight: '500'}}>{formatCurrency(item.Amount || 0)}</td>
                  <td style={{color: '#4CAF50'}}>{item.dailyAmount > 0 ? formatCurrency(item.dailyAmount) : '-'}</td>
                  <td style={{color: '#9C27B0', fontWeight: '500'}}>{item.dailyAvgPerChair > 0 ? formatCurrency(item.dailyAvgPerChair) : '-'}</td>
                  <td style={{color: '#FF9800'}}>{formatCurrency(item.MonthExpenses || 0)}</td>
                  <td style={{color: (item.Amount - item.MonthExpenses) >= 0 ? '#4CAF50' : '#F44336', fontWeight: 'bold'}}>
                    {formatCurrency((item.Amount || 0) - (item.MonthExpenses || 0))}
                  </td>
                  <td>{item.feedback || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{textAlign: 'center', color: '#777', padding: '20px'}}>Aucune donnée trouvée pour ces filtres.</p>
        )}
      </div>
    </div>
  );
};

export default WellnessStats;
